import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CloseEventArgs, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { getUid } from '@syncfusion/ej2-grids';
import { catchError, filter, finalize, forkJoin, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { UsuarioService } from 'src/app/autenticacao/usuario.service';
import { HistoricoClinicoCidDto, HistoricoClinicoConsultaDto, HistoricoClinicoEdicaoDto, HistoricoClinicoInsercaoDto } from 'src/app/dtos/historias-clinicas/historia-clinica-dto';
import { Cid10Dto } from 'src/app/dtos/doencas/cid10-dto';
import { ALERTA } from 'src/app/i18n/constantes';
import { validIntId } from 'src/app/libs/ax-validators/ax-validators';
import { ConfirmService } from 'src/app/libs/confirm/confirm.service';
import { ToastService } from 'src/app/libs/toast/toast.service';
import { RelatorioService } from 'src/app/relatorio/relatorio.service';
import { HistoriaClinicaService } from 'src/app/servicos/historia-clinica.service';
import { PacienteService } from 'src/app/servicos/paciente.service';
import { PessoaDoencaService } from 'src/app/servicos/pessoa-doenca.service';
import { HistoriaClinicaRelatorioService } from 'src/app/servicos/relatorios/historia-clinica-relatorio.service';
import { Cid10SearchComponent } from 'src/app/pesquisa/cid10-search/cid10-search.component';

interface HistoriaForm {
  id: FormControl<number>;
  pessoaId: FormControl<number>;
  data: FormControl<Date>;
  historico: FormControl<string>;
  profissionalId: FormControl<number | null>;
  profissionalNome: FormControl<string | null>;
  profissionalConselho: FormControl<string | null>;
  pessoaNome: FormControl<string>;
  pessoaCpf: FormControl<string>;
}

@Component({
  selector: 'app-historia-clinica-cadastro',
  templateUrl: './historia-clinica-cadastro.component.html',
  styleUrls: ['./historia-clinica-cadastro.component.less']
})
export class HistoriaClinicaCadastroComponent implements OnInit {
  
  modalOk: boolean = false;

  @ViewChild('modal', { static: true })
  modal!: DialogComponent;

  @ViewChild('cid10Pesquisa')
  cid10Pesquisa!: Cid10SearchComponent;

  @ViewChild('doencaHistorico')
  doencaHistorico!: any;

  id: string = getUid("id");

  submetido: boolean = false;
  emOperacao: boolean = false;
  temAlteracao: boolean = false;
  subsModelo: Subscription | null = null;
  
  tabOk: boolean = false;

  alteracaoExameLiberada: boolean = false;

  @Input()
  apenasConsulta: boolean = false;

  // CIDs manipulation
  cidsAtivos: HistoricoClinicoCidDto[] = [];
  cidsInativos: HistoricoClinicoCidDto[] = [];
  cidsAtivosValue: string[] = [];
  cidsInativosValue: string[] = [];
  cidsAtivosOriginais: Set<string> = new Set();
  cidsRecemAdicionados: Set<string> = new Set();

  modelo: FormGroup<HistoriaForm> = this.fb.group<HistoriaForm>({
    id: this.fb.nonNullable.control<number>(0),
    pessoaId: this.fb.nonNullable.control<number>(0),
    data: this.fb.nonNullable.control<Date>(new Date(), [ Validators.required ]),
    historico: this.fb.nonNullable.control<string>('', [ Validators.required ]),
    profissionalId: this.fb.control<number | null>(0),
    profissionalNome: this.fb.control<string | null>(''),
    profissionalConselho: this.fb.control<string | null>(''),
    pessoaNome: this.fb.nonNullable.control<string>(''),
    pessoaCpf: this.fb.nonNullable.control<string>('')
  });

  assuntoCadastro: Subject<boolean> = new Subject<boolean>();

  operacaoExecutada: boolean = false;

  constructor(private fb: FormBuilder, private toast: ToastService,
    private pacienteService: PacienteService,
    private confirm: ConfirmService,
    private historiaRelatorioSerivce: HistoriaClinicaRelatorioService,
    private relatorioService: RelatorioService,
    private usuarioService: UsuarioService,
    private translate: TranslateService,
    private pessoaDoencaService: PessoaDoencaService,
    private historiaClinicaService: HistoriaClinicaService) { }

  ngOnInit(): void {
    this.subsModelo = this.modelo.valueChanges.subscribe(() =>  this.temAlteracao = true);
  }

  ngOnDestroy(): void {
    this.subsModelo?.unsubscribe();
  }

  salvar(fecharAoSalvar: boolean = false) {
    this.submetido = true;

    if(!this.modelo.valid) {
      this.toast.showWarning('Favor preencher todos os campos obrigatórios.');
      return;
    }

    this.emOperacao = true;

    this.historiaClinicaService.salvarHistorico(this.gerarDto())
    .pipe(
      catchError((err) => {
        this.toast.showError('Ops, ocorreu um problema ao salvar a periodicidade.');
        this.emOperacao = false;
        throw err;
      })
    )
    .subscribe((dto) => {
      this.temAlteracao = false;
      this.operacaoExecutada = true;
      this.submetido = false;
      this.emOperacao = false;
      this.carregar(dto);
      this.toast.showSuccess('Histórico salvo com sucesso.');

      if(fecharAoSalvar)
        this.modal.hide();
    });
  }

  gerarDto(): HistoricoClinicoInsercaoDto | HistoricoClinicoEdicaoDto {
    let insercao: HistoricoClinicoInsercaoDto = {
      pessoaId: this.modelo.controls.pessoaId.value,
      data: this.modelo.controls.data.value,
      historico: this.modelo.controls.historico.value,
      cidsAtivos: this.cidsAtivos.map(c => c.cid10Id),
      cidsInativos: this.cidsInativos.map(c => c.cid10Id)
    };

    if(this.modelo.controls.id.value > 0) {
      let edicao = { ...insercao, id: this.modelo.controls.id.value };
      return edicao;
    }

    return insercao;
  }

  recarregarCids(): void {

  }

  fechandoModal(args: CloseEventArgs) {
    if(this.emOperacao) {
      args.cancel = true;
      return;
    }

    if(this.temAlteracao) {
      args.cancel = true;
      this.confirmarFechamento();
      return;
    }

    this.assuntoCadastro.next(this.operacaoExecutada);

    this.operacaoExecutada = false;
    this.submetido = false;
    this.emOperacao = false;
  }

  abrirParaConsultaEdicao(id: number): Observable<boolean> {
    this.modal.show(true);

    return new Observable<boolean>((subscriber) => {
      let subs = this.assuntoCadastro.subscribe((val) => {
        subscriber.next(val);
        subscriber.complete();
      });

      this.historiaClinicaService.consultarHistorico(id)
      .subscribe((historia => this.carregar(historia)));

      return { unsubscribe: () => subs.unsubscribe() }
    });
  }

  abrirParaInsercao(): Observable<boolean>  {
    this.modal.show(true);

    let usuario = this.usuarioService.consultarUsuario();

    this.modelo.controls.id.setValue(0, { emitEvent: false });
    this.modelo.controls.data.setValue(new Date(), { emitEvent: false });
    this.modelo.controls.historico.setValue('', { emitEvent: false });
    this.modelo.controls.profissionalId.setValue(usuario.profissionalId, { emitEvent: false });
    this.modelo.controls.profissionalNome.setValue(usuario.profissionalNome, { emitEvent: false });
    this.modelo.controls.profissionalConselho.setValue(usuario.profissionalConselho, { emitEvent: false });
    
    // Limpar CIDs
    this.cidsAtivos = [];
    this.cidsInativos = [];
    this.cidsAtivosValue = [];
    this.cidsInativosValue = [];
    this.cidsAtivosOriginais.clear();
    this.cidsRecemAdicionados.clear();

    let obs$ = new Observable<boolean>((subscriber) => {
      let subs = this.assuntoCadastro.subscribe((val) => {
        subscriber.next(val);
        subscriber.complete();
      });

      return { unsubscribe: () => subs.unsubscribe() }
    });

    return this.pacienteService.consultarPacienteEmCache()
    .pipe(
      tap((paciente) => {
        this.modelo.controls.pessoaNome.setValue(paciente.nome, { emitEvent: false });
        this.modelo.controls.pessoaCpf.setValue(paciente.cpf, { emitEvent: false });
        this.modelo.controls.pessoaId.setValue(paciente.id, { emitEvent: false });
      }),
      switchMap((paciente) => {
        // Carregar doenças ativas e inativas da pessoa
        return forkJoin({
          ativas: this.pessoaDoencaService.listarDoencasAtivas(paciente.id),
          inativas: this.pessoaDoencaService.listarDoencasInativas(paciente.id)
        });
      }),
      tap((result) => {
        // Converter doenças para HistoricoClinicoCidDto
        this.cidsAtivos = result.ativas.map(d => ({
          cid10Id: d.cid10Id,
          cid10Texto: d.cid10Texto,
          pessoaCidId: d.id
        }));
        
        this.cidsInativos = result.inativas.map(d => ({
          cid10Id: d.cid10Id,
          cid10Texto: d.cid10Texto,
          pessoaCidId: d.id
        }));
        
        // Selecionar automaticamente as doenças carregadas
        this.cidsAtivosValue = this.cidsAtivos.map(c => c.cid10Id);
        this.cidsInativosValue = this.cidsInativos.map(c => c.cid10Id);
        
        this.cidsAtivosOriginais = new Set(this.cidsAtivos.map(c => c.cid10Id));
        this.cidsRecemAdicionados.clear();
      }),
      switchMap(() => obs$)
    );
  }

  carregar(historia: HistoricoClinicoConsultaDto) {
    this.modelo.controls.id.setValue(historia.id, { emitEvent: false });
    this.modelo.controls.data.setValue(historia.data, { emitEvent: false });
    this.modelo.controls.profissionalConselho.setValue(historia.profissionalConselho, { emitEvent: false });
    this.modelo.controls.profissionalId.setValue(historia.profissionalId, { emitEvent: false });
    this.modelo.controls.profissionalNome.setValue(historia.profissionalNome, { emitEvent: false });
    this.modelo.controls.historico.setValue(historia.historico, { emitEvent: false });
    this.modelo.controls.pessoaId.setValue(historia.pessoaId, { emitEvent: false });
    this.modelo.controls.pessoaNome.setValue(historia.pessoaNome, { emitEvent: false });
    this.modelo.controls.pessoaCpf.setValue(historia.pessoaCpf, { emitEvent: false });
    
    // Carregar CIDs
    this.cidsAtivos = [...historia.cidsAtivos];
    this.cidsInativos = [...historia.cidsInativos];
    this.cidsAtivosValue = this.cidsAtivos.map(c => c.cid10Id);
    this.cidsInativosValue = this.cidsInativos.map(c => c.cid10Id);
    this.cidsAtivosOriginais = new Set(historia.cidsAtivos.map(c => c.cid10Id));
    this.cidsRecemAdicionados.clear();
  }

  excluir() {
    let id = this.modelo.controls.id.value;

    if(id <= 0)
      return;

    this.emOperacao = true;

    this.confirm.confirm()
    .pipe(
      filter(val => val === true),
      switchMap(() => this.historiaClinicaService.excluirHistorico(id)),
      catchError((err) => {
        this.toast.showError('Ops, ocorreu um problema ao excluír o registro.');
        throw err;
      }),
      finalize(() => this.emOperacao = false)
    )
    .subscribe(() => {
      this.temAlteracao = false;
      this.operacaoExecutada = true;
      this.emOperacao = false;
      this.toast.showSuccess('Registro excluído com sucesso.');
      this.modal.hide();
    })
  }

  imprimirHistoriaClinica() {
    let requisicao = this.historiaRelatorioSerivce.requisicarHistoriaClinica(this.modelo.controls.pessoaId.value);
    this.relatorioService.visualizarRelatorio(requisicao, 'História Clínica do Paciente');
  }

  confirmarFechamento() {
    this.confirm
    .confirm(this.translate.instant(ALERTA.OPERACAO_PENDENTE), { height: '220px' })
    .subscribe(val => {
      if(val) {
        this.cancelar();
      }
    })
  }

  cancelar() {
    this.temAlteracao = false;
    this.modal.hide();
  }

  // Métodos para manipular CIDs
  onCidsAtivosRemoved(e: any) {
    const removedItems = Array.isArray(e.itemData) ? e.itemData : [e.itemData];
    console.log('cidsInativos', this.cidsInativos);

    removedItems.forEach((item: HistoricoClinicoCidDto) => {
      // Remove dos valores de ativos
      this.cidsAtivosValue = this.cidsAtivosValue.filter(id => id !== item.cid10Id);
      this.cidsAtivos = this.cidsAtivos.filter(c => c.cid10Id !== item.cid10Id);

      // Se era um CID originalmente ativo e não é recém-adicionado, move para inativos
      if (!this.cidsRecemAdicionados.has(item.cid10Id)) {
        if (!this.cidsInativos.find(c => c.cid10Id === item.cid10Id)) {
          this.cidsInativos.push(item);
          this.cidsInativosValue.push(item.cid10Id);
          this.cidsInativos = [...this.cidsInativos];
          this.cidsInativosValue = [...this.cidsInativosValue];
        }
      } else {
        // Se era recém-adicionado, apenas remove
        this.cidsRecemAdicionados.delete(item.cid10Id);
      }
    });
    
    this.temAlteracao = true;
  }

  onCidsInativosRemoved(e: any) {
    const removedItems = Array.isArray(e.itemData) ? e.itemData : [e.itemData];
    
    removedItems.forEach((item: HistoricoClinicoCidDto) => {
      // Remove dos valores de inativos
      this.cidsInativosValue = this.cidsInativosValue.filter(id => id !== item.cid10Id);
      this.cidsInativos = this.cidsInativos.filter(c => c.cid10Id !== item.cid10Id);

      // Move de volta para ativos
      if (!this.cidsAtivos.find(c => c.cid10Id === item.cid10Id)) {
        this.cidsAtivos.push(item);
        this.cidsAtivosValue.push(item.cid10Id);
        this.cidsAtivos = [...this.cidsAtivos];
        this.cidsAtivosValue = [...this.cidsAtivosValue];
      }
    });
    
    this.temAlteracao = true;
  }

  abrirPesquisaCid() {
    this.cid10Pesquisa.abrirTelaCheia();
  }

  cid10Selecionado(cid: Cid10Dto) {
    const cidDto: HistoricoClinicoCidDto = {
      cid10Id: cid.codigo,
      cid10Texto: `${cid.codigo} - ${cid.descricao}`
    };

    this.adicionarCid(cidDto);
    this.cid10Pesquisa.fechar();
  }

  adicionarCid(cidDto: HistoricoClinicoCidDto) {
    // Verificar se já existe
    if (this.cidsAtivos.find(c => c.cid10Id === cidDto.cid10Id)) {
      return;
    }

    if (this.cidsInativos.find(c => c.cid10Id === cidDto.cid10Id)) {
      // Remove dos inativos e adiciona aos ativos
      this.cidsInativos = this.cidsInativos.filter(c => c.cid10Id !== cidDto.cid10Id);
      this.cidsInativosValue = this.cidsInativosValue.filter(id => id !== cidDto.cid10Id);
    }

    this.cidsAtivos.push(cidDto);
    this.cidsAtivosValue.push(cidDto.cid10Id);
    this.cidsRecemAdicionados.add(cidDto.cid10Id);
    this.cidsAtivos = [...this.cidsAtivos];
    this.cidsAtivosValue = [...this.cidsAtivosValue];
    this.temAlteracao = true;
  }

  // Evento disparado quando um CID é adicionado no texto via @
  onCidAdicionadoNoTexto(event: { codigo: string; descricao: string }): void {
    const cidDto: HistoricoClinicoCidDto = {
      cid10Id: event.codigo,
      cid10Texto: `${event.codigo} - ${event.descricao}`
    };

    this.adicionarCid(cidDto);
  }

  // Evento disparado quando um CID é removido do texto
  onCidRemovidoDoTexto(event: { codigo: string; descricao: string }): void {
    console.log('CID removido do texto:', event);
    const codigo = event.codigo;

    // Verificar se está nos ativos
    const indexAtivo = this.cidsAtivos.findIndex(c => c.cid10Id === codigo);
    if (indexAtivo !== -1) {
      const item = this.cidsAtivos[indexAtivo];
      
      // Chamar o método existente que já tem a lógica de mover para inativos se necessário
      this.onCidsAtivosRemoved({ 
        itemData: item 
      });
      return;
    }
  }

  carregarUltimoHistorico(): void {
    if(!this.modelo.controls.data.value || !this.modelo.controls.pessoaId.value) {
      this.toast.showWarning('Data ou paciente inválido para carregar o histórico.');
      return;
    }

    this.historiaClinicaService
    .obterUltimoHistorico(new Date(this.modelo.controls.data.value), this.modelo.controls.pessoaId.value)
    .subscribe((historico) => {
      if (historico) {
        this.modelo.controls.historico.setValue(historico.historico, { emitEvent: false });
        this.temAlteracao = true;
      }
    });
  }

  abrirHistoricoDoencas(): void {
    const pessoaId = this.modelo.controls.pessoaId.value;
    if (!pessoaId || pessoaId <= 0) {
      this.toast.showWarning('Paciente não identificado.');
      return;
    }

    // Verificar se há CIDs manipulados
    const temCidsManipulados = this.verificarCidsManipulados();
    
    if (temCidsManipulados) {
      this.confirm.inform(
        'As alterações nas doenças (adições, ativações ou inativações) só serão refletidas no histórico após serem salvas.',
        { 
          title: 'Atenção - Alterações Pendentes'
        }
      ).subscribe(confirmed => {
        if(confirmed)
          this.doencaHistorico.abrir(pessoaId).subscribe();
      });
      return;
    } else {
      this.doencaHistorico.abrir(pessoaId).subscribe();
    }
  }

  private verificarCidsManipulados(): boolean {
    // Verificar se há CIDs recém-adicionados
    if (this.cidsRecemAdicionados.size > 0) {
      return true;
    }

    // Verificar se os CIDs ativos mudaram em relação aos originais
    const cidsAtivosAtuais = new Set(this.cidsAtivos.map(c => c.cid10Id));
    
    // Verificar se algum CID foi removido dos ativos
    for (const cidOriginal of this.cidsAtivosOriginais) {
      if (!cidsAtivosAtuais.has(cidOriginal)) {
        return true;
      }
    }

    // Verificar se algum CID foi adicionado aos ativos (que não seja recém-adicionado)
    for (const cidAtual of cidsAtivosAtuais) {
      if (!this.cidsAtivosOriginais.has(cidAtual) && !this.cidsRecemAdicionados.has(cidAtual)) {
        return true;
      }
    }

    return false;
  }
}

